import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import type { FlowDefinition, PageFlowState } from '../../types';
import {
  normalizeFlowDefinitions,
  normalizePageFlowState,
  patchFlowValues,
  reconcilePageFlowState,
  setFlowFieldValue,
} from '../../lib/flows';
import { evaluateFlowRuntime, invokeFlowNodeAction } from '../../lib/flowNodeRuntime';
import { resolveFlowNodeSockets } from '../../lib/flowNodeDefinitions';
import { findPortalReceiversForSender } from '../../lib/globalPortalTargets';
import { scrollToSectionAnchor } from '../../lib/sectionAnchors';
import {
  loadPersistedFlowRuntimeState,
  savePersistedFlowRuntimeState,
} from '../../lib/flowRuntimePersistence';
import type { HomepageLayoutContent } from '../sections/sectionRegistry';

interface FlowContextValue {
  flows: FlowDefinition[];
  flowState: PageFlowState;
  getFlowValue: (flowId: string, fieldId: string) => unknown;
  setFlowFieldValue: (flowId: string, fieldId: string, value: unknown) => void;
  patchFlowValues: (flowId: string, values: Record<string, unknown>) => void;
  invokeFlowNode: (flowId: string, nodeId: string) => void;
}

const FlowContext = createContext<FlowContextValue | null>(null);

interface FlowProviderProps {
  layout: Pick<HomepageLayoutContent, 'flows' | 'flowState'>;
  children: React.ReactNode;
}

export const FlowProvider: React.FC<FlowProviderProps> = ({ layout, children }) => {
  const flows = useMemo(() => normalizeFlowDefinitions(layout.flows), [layout.flows]);
  const flowIds = useMemo(() => flows.map((flow) => flow.id), [flows]);
  const normalizedInitialState = useMemo(
    () => normalizePageFlowState(layout.flowState, flows),
    [layout.flowState, flows]
  );
  const persistedRuntimeState = useMemo(
    () => loadPersistedFlowRuntimeState(flowIds),
    [flowIds]
  );
  const mergedInitialState = useMemo(
    () =>
      persistedRuntimeState
        ? normalizePageFlowState(
            {
              ...normalizedInitialState,
              ...persistedRuntimeState,
            },
            flows
          )
        : normalizedInitialState,
    [flows, normalizedInitialState, persistedRuntimeState]
  );
  const [flowState, setFlowState] = useState<PageFlowState>(mergedInitialState);
  const flowSignature = useMemo(() => JSON.stringify(flows), [flows]);
  const initialStateSignature = useMemo(() => JSON.stringify(mergedInitialState), [mergedInitialState]);

  const applyRuntime = useCallback(
    (candidate: PageFlowState) => {
      const next = evaluateFlowRuntime(flows, candidate);
      return JSON.stringify(next) === JSON.stringify(candidate) ? candidate : next;
    },
    [flows]
  );

  useEffect(() => {
    setFlowState((current) => {
      const next = reconcilePageFlowState(current, flows, normalizedInitialState);
      const mergedNext = reconcilePageFlowState(next, flows, mergedInitialState);
      const withRuntime = applyRuntime(mergedNext);
      return JSON.stringify(withRuntime) === JSON.stringify(current) ? current : withRuntime;
    });
  }, [applyRuntime, flowSignature, initialStateSignature, flows, mergedInitialState, normalizedInitialState]);

  useEffect(() => {
    savePersistedFlowRuntimeState(flowState, flowIds);
  }, [flowIds, flowState]);

  const getFlowValue = useCallback(
    (flowId: string, fieldId: string) => flowState[flowId]?.values?.[fieldId],
    [flowState]
  );

  const handleSetFlowFieldValue = useCallback((flowId: string, fieldId: string, value: unknown) => {
    setFlowState((current) => applyRuntime(setFlowFieldValue(current, flowId, fieldId, value)));
  }, [applyRuntime]);

  const handlePatchFlowValues = useCallback((flowId: string, values: Record<string, unknown>) => {
    setFlowState((current) => applyRuntime(patchFlowValues(current, flowId, values)));
  }, [applyRuntime]);

  const runNodeSideEffects = useCallback(
    (flowId: string, nodeId: string, visited = new Set<string>()) => {
      const visitKey = `${flowId}:${nodeId}`;
      if (visited.has(visitKey)) return;
      visited.add(visitKey);

      const flow = flows.find((entry) => entry.id === flowId);
      const node = flow?.nodes.find((entry) => entry.id === nodeId);
      if (!flow || !node) return;

      if (node.blockType === 'show-message') {
        const message =
          typeof node.config?.message === 'string' ? node.config.message.trim() : '';
        if (message) {
          const tone = typeof node.config?.tone === 'string' ? node.config.tone : 'success';
          if (tone === 'error') {
            toast.error(message);
          } else if (tone === 'info') {
            toast(message);
          } else {
            toast.success(message);
          }
        }
      }

      if (node.blockType === 'scroll-to-section') {
        const targetId =
          typeof node.config?.targetId === 'string'
            ? node.config.targetId.trim().replace(/^#/, '')
            : '';
        if (targetId && typeof window !== 'undefined') {
          window.setTimeout(() => {
            scrollToSectionAnchor(targetId);
          }, 80);
        }
      }

      if (node.blockType === 'trigger-collector') {
        const triggerFieldId =
          node.outputs.find((binding) => binding.socketId === 'trigger')?.fieldId ?? null;
        if (!triggerFieldId) return;
        flow.nodes
          .filter((candidate) => candidate.id !== node.id)
          .filter((candidate) =>
            candidate.inputs.some((binding) => {
              if (binding.fieldId !== triggerFieldId) return false;
              const socketDefinition = resolveFlowNodeSockets(candidate, flow).find(
                (socket) => socket.direction === 'input' && socket.id === binding.socketId
              );
              return socketDefinition?.portKind === 'trigger';
            })
          )
          .forEach((candidate) => {
            runNodeSideEffects(flow.id, candidate.id, visited);
          });
        return;
      }

      if (node.blockType !== 'global-portal') return;

      findPortalReceiversForSender(flows, flowId, node.id).forEach((receiver) => {
        const triggerFieldId =
          receiver.node.outputs.find((binding) => binding.socketId === 'trigger')?.fieldId ?? null;
        if (!triggerFieldId) return;
        receiver.flow.nodes
          .filter((candidate) => candidate.id !== receiver.node.id)
          .filter((candidate) =>
            candidate.inputs.some((binding) => {
              if (binding.fieldId !== triggerFieldId) return false;
              const socketDefinition = resolveFlowNodeSockets(candidate, receiver.flow).find(
                (socket) => socket.direction === 'input' && socket.id === binding.socketId
              );
              return socketDefinition?.portKind === 'trigger';
            })
          )
          .forEach((candidate) => {
            runNodeSideEffects(receiver.flow.id, candidate.id, visited);
          });
      });
    },
    [flows]
  );

  const handleInvokeFlowNode = useCallback(
    (flowId: string, nodeId: string) => {
      runNodeSideEffects(flowId, nodeId);
      setFlowState((current) => applyRuntime(invokeFlowNodeAction(flows, current, flowId, nodeId)));
    },
    [applyRuntime, flows, runNodeSideEffects]
  );

  const value = useMemo<FlowContextValue>(
    () => ({
      flows,
      flowState,
      getFlowValue,
      setFlowFieldValue: handleSetFlowFieldValue,
      patchFlowValues: handlePatchFlowValues,
      invokeFlowNode: handleInvokeFlowNode,
    }),
    [
      flows,
      flowState,
      getFlowValue,
      handleSetFlowFieldValue,
      handlePatchFlowValues,
      handleInvokeFlowNode,
    ]
  );

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
};

export function useFlowContext(): FlowContextValue {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error('useFlowContext must be used within a FlowProvider');
  }
  return context;
}
